// Contact Form Service
// This service handles contact form submissions to WordPress backend

export interface ContactFormData {
  name: string;
  company: string;
  phone: string;
  email: string;
  emailConfirm: string;
  message: string;
  privacyPolicy: boolean;
}

export interface ContactResponse {
  success: boolean;
  message: string;
  data?: any;
}

class ContactService {
  private baseUrl: string;
  private apiUrl: string;

  constructor() {
    // Configure your WordPress site URL here
    this.baseUrl = process.env.REACT_APP_WORDPRESS_URL || 'https://your-wordpress-site.com';
    this.apiUrl = `${this.baseUrl}/wp-json/contact-form/v2`;
  }

  /**
   * Submit contact form data to WordPress
   */
  async submitContactForm(data: ContactFormData): Promise<ContactResponse> {
    // Check if mock data mode is enabled
    if (process.env.REACT_APP_USE_MOCK_DATA === 'true') {
      console.log('Mock data mode enabled - returning success without API call');
      return {
        success: true,
        message: 'お問い合わせありがとうございます。確認メールをお送りしました。',
        data: { mock: true, submittedData: data }
      };
    }

    try {
      console.log('Submitting contact form to:', this.apiUrl);
      
      const response = await fetch(`${this.apiUrl}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (response.ok) {
        return {
          success: true,
          message: result.message || 'お問い合わせありがとうございます。確認メールをお送りしました。',
          data: result.data
        };
      } else {
        return {
          success: false,
          message: result.message || '送信に失敗しました。しばらく時間をおいて再度お試しください。',
          data: result.data
        };
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      return {
        success: false,
        message: '送信に失敗しました。しばらく時間をおいて再度お試しください。'
      };
    }
  }

  /**
   * Test contact form API connection
   */
  async testConnection(): Promise<{ success: boolean; message: string; status?: number }> {
    try {
      const url = `${this.apiUrl}/test`;
      console.log('Testing contact form API connection:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        return { 
          success: true, 
          message: 'Contact form API connection successful!',
          status: response.status
        };
      } else {
        return { 
          success: false, 
          message: `Contact form API error: ${response.status} ${response.statusText}`,
          status: response.status
        };
      }
    } catch (error) {
      console.error('Contact form API connection test failed:', error);
      return { 
        success: false, 
        message: `Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }
}

// Create and export a singleton instance
export const contactService = new ContactService();

export default contactService;
